/**
 * Derive a short, ordered list of agronomic constraints from an
 * interpretation DTO for the results scan path. Pure presentation —
 * does not re-classify values.
 */
import type { SoilInterpretationDTO } from "@flaha/shared-types";

import {
	categoryToStatus,
	type StatusDescriptor,
} from "./interpretationStatus";
import { INTERPRETATION_FIELD_HELP } from "./agronomicCopy";

export interface ConstraintItem {
	field: string;
	label: string;
	value: string;
	status: StatusDescriptor;
	help?: string | undefined;
}

const CONSTRAINT_FIELDS: Array<{ field: keyof SoilInterpretationDTO; label: string }> = [
	{ field: "salinityRisk", label: "Salinity" },
	{ field: "sodiumRisk", label: "Sodium risk" },
	{ field: "phCategory", label: "pH" },
	{ field: "drainageClass", label: "Drainage" },
	{ field: "cationBalance", label: "Cation balance" },
	{ field: "waterHoldingClass", label: "Water holding" },
	{ field: "cecLevel", label: "CEC level" },
	{ field: "baseSaturationCategory", label: "Base saturation" },
];

const TONE_RANK: Record<string, number> = {
	poor: 0,
	fair: 1,
	good: 2,
	neutral: 3,
};

/**
 * Returns up to `limit` constraints, preferring poor then fair tones.
 * Good/neutral rows are omitted so the scan path stays action-focused.
 */
export function selectTopConstraints(
	interpretation: SoilInterpretationDTO | null | undefined,
	limit = 3
): ConstraintItem[] {
	if (!interpretation) return [];

	const items: ConstraintItem[] = [];
	for (const { field, label } of CONSTRAINT_FIELDS) {
		const raw = interpretation[field];
		if (typeof raw !== "string" || !raw) continue;
		// drainageClass on interpretation is a class string, not always in status tables
		const status = categoryToStatus(field as string, raw);
		if (status.tone !== "poor" && status.tone !== "fair") continue;
		items.push({
			field: field as string,
			label,
			value: raw,
			status,
			help: INTERPRETATION_FIELD_HELP[field as string],
		});
	}

	items.sort(
		(a, b) =>
			(TONE_RANK[a.status.tone] ?? 9) - (TONE_RANK[b.status.tone] ?? 9)
	);
	return items.slice(0, limit);
}
